import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'

import { selectShouldShowTutorial, setAchievementPagePreviousUrl } from '~/redux/slices/dashboard'
import { Link } from '~/components'
import useCurrentUser from '~/hooks/useCurrentUser'
import ROUTES from '~/shared/constants/routes.const'
import ArrowButton from '~/checklist/components/ArrowButton'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import HouseOfMathLoader from '~/components/HouseOfMathLoader'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { EventDetectionMethod, EventType } from '~/behavior-tracking/types'
import { logUserActivity } from '~/behavior-tracking/utility/view-event-logs.utility'
import useGetCorrectedCountryCode from '~/curriculum/hooks/useGetCorrectedCountryCode'

import { useClaimCertificate } from '../hooks/useClaimCertificate'
import { selectCurriculumSubjectContent, selectGradeContent } from '../slice/curriculum.slice'
import { useGetCurriculumCountryCodeFromUrlWithoutPrefix } from '../hooks/useGetCurriculumCountryCode'
import { useGetCurriculumCertificateQuery } from '../apis/curriculum.api'

import CurriculumCompetencyList from './CurriculumCompetencyList'

export default function MQCurriculumMathTopicList({
  viewKey,
  gradeId,
  mappingQuizApplicable,
  masteryTrackApplicable,
}: {
  viewKey: string
  gradeId: string
  mappingQuizApplicable: boolean
  masteryTrackApplicable: boolean
}): React.ReactElement {
  const { t } = useTranslation('curriculum')
  const user = useCurrentUser()

  const dispatch = useAppDispatch()
  const router = useRouter()
  const subject = useAppSelector(selectCurriculumSubjectContent)
  const gradeData = useAppSelector(selectGradeContent)
  const shouldShowTutorial = useAppSelector(selectShouldShowTutorial)

  const countryCode = useGetCurriculumCountryCodeFromUrlWithoutPrefix()

  const totalSubTopics = subject?.competencyGoals?.length || 0
  const correctedCountryCode = useGetCorrectedCountryCode(countryCode)
  const curriculumCertificateResponse = useGetCurriculumCertificateQuery({
    gradeLevel: gradeData.gradeLevel,
    subjectOrder: subject?.subjectOrder,
    countryCode: correctedCountryCode,
  })?.data

  const [isExpanded, setIsExpanded] = useState(true)

  const { claimCertificate, isLoading: generatingCertificate } = useClaimCertificate()

  const currentProgressCount = subject?.competencyGoals?.reduce(
    (accumulator, data) => accumulator + (data.isCompleted ? 1 : 0),
    0,
  )
  const { topicId, competencyGoals } = subject
  const onMasteryCertificateClick = async (): Promise<void> => {
    if (user?.id) {
      const { subjectLabel, subjectOrder } = subject
      const subdivisionOrder = subject.competencyGoals[0].subdivisionOrder

      const extra = {
        title: subjectLabel,
        countryCode,
        gradeLevel: gradeData.gradeLevel,
        subjectOrder,
        quarterNo: subdivisionOrder,
      }
      const event = {
        eventName: EventType.CURRICULUM_EVENT,
        viewKey,
        extra: {
          detectBy: EventDetectionMethod.ON_CURRICULUM_CERTIFICATE_CLICK,
          ...extra,
        },
      }
      await logUserActivity(user.id, event)
    }
  }

  useEffect(() => {
    const getCertificate = async (): Promise<void> => {
      if (
        !gradeId ||
        !countryCode ||
        !gradeData.gradeLevel ||
        !subject?.subjectLabel ||
        !subject.subjectOrder ||
        !subject.topicId
      ) {
        return
      }
      await claimCertificate({
        gradeId,
        countryCode,
        gradeLevel: gradeData.gradeLevel,
        subject: subject.subjectLabel,
        subjectOrder: subject.subjectOrder,
        topicId: subject.topicId,
      })
    }

    if (
      currentProgressCount === totalSubTopics &&
      curriculumCertificateResponse &&
      !curriculumCertificateResponse.isCompleted &&
      subject?.subjectOrder > 0
    ) {
      getCertificate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProgressCount, curriculumCertificateResponse, gradeData.gradeLevel, totalSubTopics])

  return (
    <div className="relative bg-purple-darker w-19/20 2xl:w-4/5 mx-auto rounded-17 border border-white shadow-lg mt-8">
      <div className="h-20">
        {generatingCertificate && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80"
            style={{ zIndex: 99999 }}
          >
            <HouseOfMathLoader />
          </div>
        )}

        <button
          className="flex h-full w-full items-center cursor-pointer px-3"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="w-1/3 md:w-1/4 flex items-center justify-start md:px-5 gap-2 md:gap-3">
            {mappingQuizApplicable && (
              <>
                <ResponsiveImage
                  alt="Graduation Cap"
                  height={590}
                  imageClassName="w-12 md:w-20"
                  srcKey={`curriculum/hats/black-hat-${subject.topicId}.png`}
                  width={960}
                />
                <span className="text-white tracking-widest md:text-8">
                  {currentProgressCount}/{totalSubTopics}
                </span>
              </>
            )}
          </div>
          <h3 className="text-white text-center md:text-8 font-semibold tracking-widest w-2/3 md:w-1/2">
            {t('mappingQuiz.stepTwo')}
          </h3>
          <div className="w-1/3 md:w-1/4 flex justify-end">
            <ArrowButton color="text-white" expanded={isExpanded} size={24} />
          </div>
        </button>
      </div>
      {isExpanded && (
        <div>
          {shouldShowTutorial && (
            <div className="absolute w-full top-0 h-70 border opacity-0 rounded-7 curriculum-pop-up-tutorial-caps" />
          )}
          <p className="text-white font-light lg:text-6 tracking-widest text-center my-5 md:my-7">
            {t('mappingQuiz.completeSubTopics')}
          </p>

          <div className="space-y-3 md:space-y-6 mt-10 w-full mx-auto">
            <CurriculumCompetencyList
              competencyList={competencyGoals}
              masteryTrackApplicable={masteryTrackApplicable}
              topicId={topicId}
              uniqueSubjectId={`subject_${subject?.subjectOrder}_quarter`}
            />
          </div>
          {masteryTrackApplicable && (
            <div className="flex justify-center">
              <div className="flex flex-col justify-center items-center text-center mt-6 mb-10 md:mt-10 text-white tracking-wider text-md curriculum-pop-up-tutorial-certificate">
                {currentProgressCount === totalSubTopics ? (
                  <Link
                    className="focus:outline-none"
                    onClick={() => {
                      onMasteryCertificateClick()
                      dispatch(setAchievementPagePreviousUrl(router.asPath))
                    }}
                    to={ROUTES.ACHIEVEMENTS}
                  >
                    <ResponsiveImage
                      alt="Certificate"
                      height={300}
                      imageClassName="w-25 md:w-40"
                      srcKey={`curriculum/certificate/mastery-certificate-${subject.topicId}_v4.png`}
                      width={300}
                    />
                  </Link>
                ) : (
                  <ResponsiveImage
                    alt="Certificate"
                    height={300}
                    imageClassName="w-25 md:w-40"
                    srcKey={`curriculum/certificate/yet-to-achieve-certificate-${subject.topicId}_v2.png`}
                    width={300}
                  />
                )}
                <p> {t('mappingQuiz.masteryCertificate')}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
